import React from 'react';

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCaretDown, faCaretUp} from '@fortawesome/free-solid-svg-icons';

import {PrimaryButton} from 'Components/Button';
import {FormHandler, Input, Label, FileUpload} from 'Components/Form';
import {Alert, Loading} from 'Components/Partials';
import {DisplayTimeZone, DisplayLanguage} from 'Components/Settings';

import {getFallbackAvatarUrl} from 'Services/AuthHelpers';
import Me from 'Services/Api/Me/Me';

import SettingsLayout from 'Components/Layouts/SettingsLayout';
import AdminLayout from 'Pages/Admin/AdminLayout';

class Profile extends React.Component {
    /**
     * @var success
     * @type {string}
     */
    success = 'Your details have been updated successfully';

    /**
     * @var displayAvatar
     * @type {boolean}
     */
    displayAvatar = true;

    /**
     * @var state
     */
    state = {
        loading: true,
        user_id: this.props.match?.params?.user ?? null,
        invitesShown: []
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        this.loadUser();
    };

    /**
     * @method loadUser
     */
    loadUser = async () => {
        const {user_id} = this.state;

        // Request the data.
        const response = await Me.getMe({
            includeHasInvited: true,
            user_id
        });

        // Increment the invites array keys as we are 1 based for this form.
        let invites = [];

        if (response.data.data.has_invited) {
            for (var i=0; i < response.data.data.has_invited.length; i++) {
                invites[i+1] = response.data.data.has_invited[i];
            }
        }

        // Set the form values.
        this.props.setForm({
            ...response.data.data,
            invites,
            user_id
        });

        // Finish loading.
        this.setState({
            loading: false
        });
    }

    /**
     * @method getFormValues
     * @param {integer|null} index
     * @return {object}
     */
    getFormValues = (index) => {
        const {form} = this.props;

        return index !== null ? 
            (form['invites'] && form['invites'][index] ? 
                form['invites'][index] : 
                null) : 
            form;
    }

    /**
     * @method getFormkey
     * @param {integer|null} index
     * @return {string}
     */
    getFormkey = (index) => {
        return index !== null ? 
            'invites.' + index + '.' : 
            '';
    }

    /**
     * @method handleToggleInvite
     * @param {integer} index
     */
    handleToggleInvite = (index) => {
        const {invitesShown} = this.state;

        this.setState({
            invitesShown: {
                ...invitesShown,
                [index]: !invitesShown[index]
            }
        });
    }

    /**
     * @method render
     * @return {JSX.Element}
     */
    render () {
        const {user_id} = this.state;

        if (user_id) {
            return (
                <AdminLayout title='Users - Profile'>
                    {this.renderPage()}
                </AdminLayout>
            );
        }

        return (
            <SettingsLayout title="Registration">
                <div className="divide-y divide-gray-200 lg:col-span-9">
                    <div className="p-6">
                        <h2 className="text-lg leading-6 font-medium text-gray-900">
                            Profile
                        </h2>
                    </div>

                    {this.renderPage()}
                </div>
            </SettingsLayout>
        );
    }

    /**
     * @method renderPage
     * @return {JSX.Element}
     */
    renderPage () {
        const {loading} = this.state;
        const {form, working, alert, handleInput, handleSubmit} = this.props;

        if (loading) {
            return <Loading />;
        }

        return (
            <form
                autoComplete="off"
                className="divide-y divide-gray-200"
                onSubmit={(e) => handleSubmit(e, Me.update, this.success)}
            >
                <div className="p-6">
                    {alert !== null && (<Alert {...alert} />)}

                    {this.displayAvatar && 
                        <FileUpload
                            containerClassName="m-8 mt-4 flex flex-col justify-center items-center gap-4"
                            value={form.avatar_path}
                            onChange={v => handleInput('avatar_path', v)}
                            accept="image/*"
                            placeholder="Update Picture"
                            fallbackImage={getFallbackAvatarUrl(form)}
                        />
                    }

                    {this.renderForm(null)}

                    {form.number_of_invites > 0 && this.renderInvites()}
                </div>

                <div className="p-6 flex justify-between">
                    <PrimaryButton
                        text="Save"
                        working={working}
                    />
                </div>
            </form>
        );
    }

    /**
     * @method renderPage
     * @return {JSX.Element}
     */
    renderInvites () {
        const {invitesShown} = this.state;
        const {form} = this.props;

        return (
            <>
                {[...Array(form.number_of_invites)].map((e, i) => (
                    <div key={i}>
                        <div
                            className={`
                                text-white text-3xl bg-gray-500 w-full cursor-pointer px-4 py-2 mt-8 mb-4 
                                select-none flex flex-row justify-between items-center
                            `}
                            onClick={() => this.handleToggleInvite(i)}
                        >
                            <p>
                                Invite {i+1}
                            </p>

                            <FontAwesomeIcon
                                icon={invitesShown[i] ? faCaretUp : faCaretDown}
                            />
                        </div>

                        {invitesShown[i] && 
                            <div className="bg-gray-300 p-4">
                                {this.renderForm((i+1))}
                            </div>
                        }
                    </div>
                ))}
            </>
        );
    }

    /**
     * @method renderPage
     * @param {integer|null} index
     * @param {string} group_name
     * @return {JSX.Element}
     */
    renderForm (index, group_name) {
        const {user_id} = this.state;
        const {form, getFieldError, handleInput} = this.props;

        let values = this.getFormValues(index);
        let key = this.getFormkey(index);

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Input
                    label="First Name"
                    value={values?.first_name}
                    id="first_name"
                    error={getFieldError(key + 'first_name')}
                    onChange={(v) => handleInput(key + 'first_name', v)}
                />

                <Input
                    label="Last Name"
                    value={values?.last_name}
                    id="last_name"
                    error={getFieldError(key + 'last_name')}
                    onChange={(v) => handleInput(key + 'last_name', v)}
                />

                <Input
                    label="Email"
                    value={values?.email}
                    id="email"
                    error={getFieldError(key + 'email')}
                    onChange={(v) => handleInput(key + 'email', v)}
                    readOnly={!window.base.features.allow_email_change && !key}
                />

                {!user_id && !key &&
                    <>
                        <div>
                            <Label label="TimeZone" htmlFor="timezone" />
                            <DisplayTimeZone editMode={true} />
                        </div>

                        {Object.keys(window.base.languages).length > 1 &&
                            <div>
                                <Label label="Language" htmlFor="language" />
                                <DisplayLanguage editMode={true} />
                            </div>
                        }
                    </>
                }
            </div>
        );
    }
}

export default FormHandler(Profile);
